import React from 'react';
import {
  Progress, Typography, Space, Divider,
} from 'antd';
import {
  CheckCircleOutlined,
  WarningOutlined, ArrowRightOutlined, RiseOutlined, MinusOutlined,
} from '@ant-design/icons';
import { JobStatus, JOB_TYPE_LABEL } from '../../types/job';
import { Card } from '../../types/card';
import { DROP_STATUSES, FONT_SIZE, FONT_WEIGHT, SPACING, RADIUS } from '../../constants/constants';
import { useThemeTokens } from '../../hooks/UseThemeTokens';
import { AppButton } from '../common/AppButton';
import { AppTag } from '../common/AppTag';
import { AppModal } from '../common/AppModal';

const { Text } = Typography;

// ─── 타입 ─────────────────────────────────────────────────────────────────────

interface Props {
  /** null 이면 모달 닫힘 */
  job:              JobStatus | null;
  open:             boolean;
  onClose:          () => void;
  onGoToDropReview?: () => void;
}

// ─── 유틸 ─────────────────────────────────────────────────────────────────────

function summarizeResults(job: JobStatus) {
  if (!job.results) return null;
  const increased = job.results.filter((c) => c.priceChangeStatus === 'INCREASED').length;
  const dropped   = job.results.filter((c: Card) => DROP_STATUSES.includes(c.priceChangeStatus)).length;
  const unchanged = job.results.length - increased - dropped;
  return { increased, dropped, unchanged };
}

// ─── SummaryCard ──────────────────────────────────────────────────────────────

const SummaryCard: React.FC<{
  value: number;
  label: string;
  icon: React.ReactNode;
  theme: { bg: string; text: string; border: string };
  badge?: boolean;
  fallbackBg?: string;
  fallbackBorder?: string;
}> = ({ value, label, icon, theme, badge, fallbackBg, fallbackBorder }) => {
  const t = useThemeTokens();
  return (
  <div style={{
    flex: 1, borderRadius: RADIUS.lg, padding: `${SPACING.xs}px ${SPACING.sm}px`, textAlign: 'center',
    background: fallbackBg ?? theme.bg,
    border: `1px solid ${fallbackBorder ?? theme.border}`,
  }}>
    <div style={{ color: theme.text, fontSize: FONT_SIZE.lg, fontWeight: FONT_WEIGHT.bold }}>{value}</div>
    <div style={{ color: theme.text, fontSize: FONT_SIZE.sm, marginTop: SPACING.xxs, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: SPACING.xxs }}>
      {icon} {label}
      {badge && (
        <span style={{
          background: theme.text, color: t.white,
          borderRadius: RADIUS.sm, padding: `0 ${SPACING.xxs}px`,
          fontSize: FONT_SIZE.xs, fontWeight: FONT_WEIGHT.bold,
        }}>확인 필요</span>
      )}
    </div>
  </div>
  );
};

// ─── JobResultBody ────────────────────────────────────────────────────────────

/**
 * [4번 개선] RUNNING 상태를 제거하고 완료·실패 결과 표시만 담당한다.
 * 진행 중 표시는 JobProgressBar(상단 고정 바)가 맡는다.
 */
const JobResultBody: React.FC<{
  job: JobStatus;
  hasDrops: boolean;
  onClose: () => void;
  onGoToDropReview?: () => void;
}> = ({ job, hasDrops, onClose, onGoToDropReview }) => {
  const tk         = useThemeTokens();
  const subColor   = tk.textSecondary;
  const titleColor = tk.textPrimary;
  const tIncrease  = tk.tag('resultIncrease');
  const tDrop      = tk.tag('resultDrop');
  const tUnchanged = tk.tag('resultUnchanged');

  const percent  = job.total > 0 ? Math.round((job.processedCount / job.total) * 100) : 100;
  const summary  = job.type === 'UPDATE_PRICES' ? summarizeResults(job) : null;
  // naver.commerce.register-enabled=false라 실제 Naver 호출 없이 success=true로만
  // 끝난 경우(2026-08-19, NAVER_REGISTER_ENABLED 환경변수 누락 사고 이후 추가) —
  // 진행 바·태그를 초록 "성공"이 아니라 주황 "DRY-RUN"으로 보여준다.
  const isDryRun = job.status === 'DONE' && job.dryRunCount > 0;
  const realSuccessCount = job.successCount - job.dryRunCount;

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      {/* 완료 진행 바 — 결과 요약의 시각적 닻 */}
      <Progress
        percent={percent}
        status={job.status === 'FAILED' ? 'exception' : isDryRun ? 'normal' : 'success'}
        strokeColor={isDryRun ? tk.warning : undefined}
        format={() => `${job.processedCount} / ${job.total}`}
      />

      {isDryRun && (
        <div style={{
          background: tk.tintWarning, border: `1px solid ${tk.lineWarning}`,
          borderRadius: RADIUS.lg, padding: `${SPACING.xs}px ${SPACING.sm}px`,
          fontSize: FONT_SIZE.sm, color: tk.warning, fontWeight: FONT_WEIGHT.semibold,
        }}>
          <WarningOutlined /> DRY-RUN 상태입니다 — 실제로는 Naver에 아무것도 등록되지
          않았습니다. 서버 설정(NAVER_REGISTER_ENABLED)을 확인해주세요.
        </div>
      )}

      {job.status === 'DONE' && (
        <Space>
          {realSuccessCount > 0 && (
            <AppTag variant="jobResult" type="success" count={realSuccessCount} />
          )}
          {job.dryRunCount > 0 && (
            <AppTag variant="jobResult" type="dryRun" count={job.dryRunCount} />
          )}
          {job.failCount > 0 && (
            <AppTag variant="jobResult" type="error" count={job.failCount} />
          )}
        </Space>
      )}

      {/* ① 가격 받기의 "실패"는 대부분 시세 API(PriceHub)에 아직 등록되지 않은 상품이라
          가격 자체를 못 받아온 경우다 — 이유 설명 없이 숫자만 보이면 작업자가 오류로
          오해하기 쉬워 원인을 짚어준다(2026-07-30, 사용자 피드백). */}
      {/* PriceHub 가 이 매장을 알아보지 못해 공통가를 대신 받은 카드 — <매장> 가격 자리에 쓰지만 출처를 분명히(감사 #181). */}
      {job.type === 'UPDATE_PRICES' && (job.storeUnresolvedCount ?? 0) > 0 && (
        <div data-testid="store-unresolved" style={{
          background: tk.tintWarning, border: `1px solid ${tk.lineWarning}`,
          borderRadius: RADIUS.lg, padding: `${SPACING.xs}px ${SPACING.sm}px`,
          fontSize: FONT_SIZE.sm, color: tk.warning, fontWeight: 600,
        }}>
          <WarningOutlined /> PriceHub 가격 · 매장 미인식 {(job.storeUnresolvedCount ?? 0).toLocaleString()}건 — PriceHub 가 이 매장을
          알아보지 못해 매장 가격 대신 PriceHub 가격을 받았습니다. ③ 반영 전에 확인하세요.
        </div>
      )}
      {job.type === 'UPDATE_PRICES' && job.failCount > 0 && (
        <div style={{
          background: tk.tintWarning, border: `1px solid ${tk.lineWarning}`,
          borderRadius: RADIUS.lg, padding: `${SPACING.xs}px ${SPACING.sm}px`,
          fontSize: FONT_SIZE.sm, color: tk.warning,
        }}>
          <WarningOutlined /> 미등록 상품 {job.failCount}건은 시세 정보가 없어 가격을 불러오지 못했습니다
        </div>
      )}

      {summary && (
        <>
          <Divider style={{ margin: `${SPACING.xxs}px 0` }} />
          <div style={{ fontSize: FONT_SIZE.sm, fontWeight: FONT_WEIGHT.semibold, marginBottom: SPACING.xs, color: titleColor }}>
            가격 변동 요약
          </div>
          <div style={{ display: 'flex', gap: SPACING.xs }}>
            <SummaryCard value={summary.increased} label="상승" icon={<RiseOutlined />} theme={tIncrease} />
            <SummaryCard
              value={summary.dropped} label="하락" icon={<WarningOutlined />}
              theme={hasDrops ? tDrop : tUnchanged}
              badge={hasDrops}
              fallbackBg={tk.bgSurface}
              fallbackBorder={tk.border}
            />
            <SummaryCard value={summary.unchanged} label="유지" icon={<MinusOutlined />} theme={tUnchanged} />
          </div>
          {hasDrops && (
            <div style={{
              background: tDrop.bg, border: `1px solid ${tDrop.border}`,
              borderRadius: RADIUS.lg, padding: `${SPACING.xs}px ${SPACING.sm}px`,
            }}>
              <div style={{ color: tDrop.text, fontWeight: FONT_WEIGHT.semibold, fontSize: FONT_SIZE.sm }}>
                <WarningOutlined /> 하락 {summary.dropped}건 — 각 항목의 적용 여부를 선택해주세요
              </div>
              <div style={{ color: subColor, fontSize: FONT_SIZE.sm, marginTop: SPACING.xxs }}>
                Naver 반영 전에 가격을 확인해야 합니다
              </div>
            </div>
          )}
        </>
      )}

      {job.status === 'FAILED' && (
        <Text type="danger">오류 발생: {job.error}</Text>
      )}
    </Space>
  );
};

// ─── JobModal ─────────────────────────────────────────────────────────────────

/**
 * [4번 개선] 완료·실패 결과 요약 전용 모달.
 *
 * 진행 중(RUNNING) 상태는 이 모달이 아닌 JobProgressBar(상단 고정 바)로 표시된다.
 * CardListPage 에서 JobProgressBar.onOpenDetail 콜백이 이 모달을 연다.
 */
export const JobModal: React.FC<Props> = ({ job, open, onClose, onGoToDropReview }) => {
  const t = useThemeTokens();
  if (!job) return null;

  const summary  = job.type === 'UPDATE_PRICES' ? summarizeResults(job) : null;
  const hasDrops = (summary?.dropped ?? 0) > 0;
  const typeLabel = JOB_TYPE_LABEL[job.type] ?? job.type;
  const isDryRun = job.status === 'DONE' && job.dryRunCount > 0;
  const titleIcon = isDryRun
    ? <WarningOutlined style={{ color: t.warning, marginRight: SPACING.xxs }} />
    : job.status === 'DONE'
    ? <CheckCircleOutlined style={{ color: t.success, marginRight: SPACING.xxs }} />
    : null;

  return (
    <AppModal
      open={open}
      title={<span>{titleIcon}{typeLabel} {job.status === 'DONE' ? (isDryRun ? '완료 (DRY-RUN)' : '완료') : '실패'}</span>}
      footer={[
        <AppButton key="close" variant="default" onClick={onClose}>
          {hasDrops ? '나중에 확인' : '닫기'}
        </AppButton>,
        ...(hasDrops && onGoToDropReview ? [
          <AppButton key="review" variant="danger" icon={<ArrowRightOutlined />}
            onClick={() => { onClose(); onGoToDropReview(); }}>
            하락 {summary!.dropped}건 확인하기
          </AppButton>,
        ] : []),
      ]}
      onCancel={onClose}
      width={480}
    >
      <JobResultBody
        job={job}
        hasDrops={hasDrops}
        onClose={onClose}
        onGoToDropReview={onGoToDropReview}
      />
    </AppModal>
  );
};